// Consultas do painel inicial. Só leituras de endpoints reais; cada uma só dispara com `enabled` (autorização no cliente
// é dica de interface — o servidor continua sendo a autoridade). Erro nunca vira zero: o cartão mostra "indisponível".
import type { components } from '@giyus/api-client';
import { useQuery } from '@tanstack/react-query';

import { useApiClient } from '../lib/api/apiClientContext';
import { previewRequest } from '../lib/api/previewRequest';
import { WORKFLOW_PREVIEW_PATHS } from '../modules/workflow/api/contract.preview';
import { type HomeInboxItem, normalizeInboxItems } from './homeData';

type Schemas = components['schemas'];

export type StudiesOverview = Schemas['StudiesOverviewResponse'];
export type BreakGlassEvent = Schemas['AuthorizationBreakGlassEventResponse'];
export type AccessReviewItem = Schemas['AuthorizationAccessReviewItemResponse'];
export type TwoKeyApproval = Schemas['AuthorizationTwoKeyApprovalResponse'];
export type MyDelegation = Schemas['AuthorizationMyDelegationResponse'];
export type TenantOverview = Schemas['TenancyTenantOverviewResponse'];

function required<T>(data: T | undefined): T {
  if (data === undefined) {
    throw new Error('Empty response');
  }
  return data;
}

const HOME = ['home'] as const;

/** `GET /me/inbox` (tarefas, aprovações e exceções abertas do principal), já normalizado. */
export function useHomeInbox(enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...HOME, 'inbox'],
    queryFn: async (): Promise<HomeInboxItem[]> =>
      normalizeInboxItems(
        await previewRequest<unknown>(client, 'get', WORKFLOW_PREVIEW_PATHS.meInbox),
      ),
    enabled,
    meta: { silent: true },
  });
}

/** `GET /network/studies/overview` — agregados reais do módulo Estudos (rede). */
export function useStudiesOverview(enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...HOME, 'studies-overview'],
    queryFn: async (): Promise<StudiesOverview> => {
      const { data } = await client.GET('/api/v1/network/studies/overview');
      return required(data);
    },
    enabled,
    meta: { silent: true },
  });
}

/** Acessos emergenciais aguardando revisão (QA/DPO da rede). */
export function useBreakGlassPending(enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...HOME, 'break-glass-pending'],
    queryFn: async (): Promise<BreakGlassEvent[]> => {
      const { data } = await client.GET('/api/v1/network/authz/break-glass', {
        params: { query: { status: 'PendingReview' } },
      });
      return required(data);
    },
    enabled,
    meta: { silent: true },
  });
}

/** Itens de recertificação de acesso atribuídos ao próprio usuário. */
export function useMyAccessReviews(enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...HOME, 'my-access-reviews'],
    queryFn: async (): Promise<AccessReviewItem[]> => {
      const { data } = await client.GET('/api/v1/authz/access-reviews/my-items');
      return required(data);
    },
    enabled,
    meta: { silent: true },
  });
}

/** Aprovações de duas chaves que aguardam a segunda chave do usuário. */
export function useTwoKeyAwaitingMe(enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...HOME, 'two-key-awaiting'],
    queryFn: async (): Promise<TwoKeyApproval[]> => {
      const { data } = await client.GET('/api/v1/authz/two-key', {
        params: { query: { mine: false, status: 'PendingSecondKey' } },
      });
      return required(data);
    },
    enabled,
    meta: { silent: true },
  });
}

/** Delegações assinadas pelo PI que aguardam o aceite do usuário. */
export function useDelegationsAwaitingMe(enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...HOME, 'delegations-pending'],
    queryFn: async (): Promise<MyDelegation[]> => {
      const { data } = await client.GET('/api/v1/me/delegations', {
        params: { query: { status: 'PendingAcceptance' } },
      });
      return required(data);
    },
    enabled,
    meta: { silent: true },
  });
}

/** Painel de rede dos tenants (primeira página): situação e pendência de administrador. */
export function useTenantsOverviewHome(enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...HOME, 'tenants-overview'],
    queryFn: async (): Promise<TenantOverview> => {
      const { data } = await client.GET('/api/v1/network/tenants-overview', {
        params: { query: {} },
      });
      return required(data);
    },
    enabled,
    meta: { silent: true },
  });
}
